import { functionsBase } from '../config/functionsBase';

const FUNCTIONS_BASE = functionsBase('/.netlify/functions');

function getAccessToken() {
  try {
    const raw = localStorage.getItem('discordAdminAuth');
    if (!raw) return null;
    const data = JSON.parse(raw);
    const maxAge = 7 * 24 * 60 * 60 * 1000;
    if (!data?.accessToken || Date.now() - data.timestamp > maxAge) {
      localStorage.removeItem('discordAdminAuth');
      return null;
    }
    return data.accessToken;
  } catch {
    return null;
  }
}

function getAdminUser() {
  try {
    const raw = localStorage.getItem('discordAdminAuth');
    if (!raw) return null;
    const data = JSON.parse(raw);
    const maxAge = 7 * 24 * 60 * 60 * 1000;
    if (!data?.user?.id || Date.now() - data.timestamp > maxAge) {
      localStorage.removeItem('discordAdminAuth');
      return null;
    }
    return data.user;
  } catch {
    return null;
  }
}

function setAdminAuth(user, accessToken) {
  localStorage.setItem(
    'discordAdminAuth',
    JSON.stringify({ user, accessToken, timestamp: Date.now() })
  );
  try {
    window.dispatchEvent(
      new CustomEvent('nexus:admin-auth', { detail: { authenticated: true } })
    );
  } catch {
    /* ignore */
  }
}

function clearAdminAuth() {
  localStorage.removeItem('discordAdminAuth');
  try {
    window.dispatchEvent(
      new CustomEvent('nexus:admin-auth', { detail: { authenticated: false } })
    );
  } catch {
    /* ignore */
  }
}

async function adminFetch(path, options = {}) {
  const token = getAccessToken();
  if (!token) {
    const err = new Error('Sessão admin expirada. Faça login novamente.');
    err.code = 'NO_TOKEN';
    throw err;
  }

  const res = await fetch(`${FUNCTIONS_BASE}/${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Erro ${res.status}`);
    err.status = res.status;
    err.data = data;
    if (res.status === 401) {
      err.code = 'UNAUTHORIZED';
    } else if (res.status === 403) {
      // Área específica vs ação vs acesso total ao painel
      if (data.code === 'ACTION_FORBIDDEN') {
        err.code = 'ACTION_FORBIDDEN';
      } else if (
        data.code === 'TAB_FORBIDDEN' ||
        String(data.error || '').includes('esta área')
      ) {
        err.code = 'TAB_FORBIDDEN';
      } else {
        err.code = 'FORBIDDEN';
      }
    } else {
      err.code = 'API';
    }
    throw err;
  }
  return data;
}

async function fetchStats() {
  return adminFetch('admin-stats');
}

async function fetchTickets({ status = 'all', limit = 50, skip = 0, q = '' } = {}) {
  const params = new URLSearchParams({
    status,
    limit: String(limit),
    skip: String(skip),
  });
  if (q) params.set('q', q);
  return adminFetch(`admin-tickets?${params}`);
}

async function fetchTicket(number) {
  return adminFetch(`admin-ticket?number=${encodeURIComponent(number)}`);
}

async function ticketAction({ action, number, content } = {}) {
  return adminFetch('admin-ticket-action', {
    method: 'POST',
    body: JSON.stringify({ action, number, content }),
  });
}

async function deleteTickets(numbers = []) {
  return adminFetch('admin-tickets-delete', {
    method: 'POST',
    body: JSON.stringify({ numbers }),
  });
}

async function deleteRatings({ numbers = [], mode } = {}) {
  const body = mode === 'orphans' ? { mode: 'orphans' } : { numbers };
  return adminFetch('admin-ratings-delete', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

async function fetchSettings() {
  return adminFetch('admin-settings');
}

async function updateSettings(patch) {
  return adminFetch('admin-settings', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

async function fetchRatings({ limit = 40 } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  return adminFetch(`admin-ratings?${params}`);
}

async function fetchSiteConfigAdmin() {
  return adminFetch('admin-site');
}

async function updateSiteConfig(patch) {
  return adminFetch('admin-site', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

async function fetchAuditSettings() {
  return adminFetch('admin-audit-settings');
}

async function updateAuditSettings(patch) {
  return adminFetch('admin-audit-settings', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

async function fetchGuildResources() {
  return adminFetch('admin-guild-resources');
}

async function fetchGuildMembers() {
  return adminFetch('admin-guild-members');
}

async function fetchWelcomeSettings() {
  return adminFetch('admin-welcome-settings');
}

async function updateWelcomeSettings(patch) {
  return adminFetch('admin-welcome-settings', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

async function fetchSession() {
  return adminFetch('admin-session');
}

async function fetchPermissions() {
  return adminFetch('admin-permissions');
}

async function updatePermissions(patch) {
  return adminFetch('admin-permissions', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

async function fetchVip({ status = 'active', planId = '', q = '', limit = 40, skip = 0 } = {}) {
  const params = new URLSearchParams({
    status,
    limit: String(limit),
    skip: String(skip),
  });
  if (planId) params.set('planId', planId);
  if (q) params.set('q', q);
  return adminFetch(`admin-vip?${params}`);
}

async function fetchPerformance({ serverId = '', hours = 2, historyLimit = 240 } = {}) {
  const params = new URLSearchParams({
    hours: String(hours),
    historyLimit: String(historyLimit),
  });
  if (serverId) params.set('serverId', serverId);
  return adminFetch(`admin-performance?${params}`);
}

async function updateVip(patch) {
  return adminFetch('admin-vip', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

async function fetchProfiles({
  presence = 'all',
  q = '',
  limit = 40,
  skip = 0,
  codes = true,
} = {}) {
  const params = new URLSearchParams({
    presence,
    limit: String(limit),
    skip: String(skip),
  });
  if (q) params.set('q', q);
  if (codes) params.set('codes', '1');
  return adminFetch(`admin-profiles?${params}`);
}

async function updateProfiles(patch) {
  return adminFetch('admin-profiles', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

async function fetchTeam({ visibility = 'all', q = '', limit = 80, skip = 0 } = {}) {
  const params = new URLSearchParams({
    visibility,
    limit: String(limit),
    skip: String(skip),
  });
  if (q) params.set('q', q);
  return adminFetch(`admin-team?${params}`);
}

async function updateTeam(patch) {
  return adminFetch('admin-team', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

async function fetchChangelog({ area = '', actorId = '', q = '', limit = 50, skip = 0 } = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    skip: String(skip),
  });
  if (area) params.set('area', area);
  if (actorId) params.set('actorId', actorId);
  if (q) params.set('q', q);
  return adminFetch(`admin-changelog?${params}`);
}

async function fetchMarketing({
  section = 'overview',
  q = '',
  category = '',
  creatorId = '',
  days = 30,
  type = '',
  month = '',
  from = '',
  to = '',
  limit = 50,
  skip = 0,
} = {}) {
  const params = new URLSearchParams({
    section,
    limit: String(limit),
    skip: String(skip),
  });
  if (q) params.set('q', q);
  if (category) params.set('category', category);
  if (creatorId) params.set('creatorId', creatorId);
  if (section === 'overview' && days) params.set('days', String(days));
  if (section === 'export') {
    if (type) params.set('type', type);
    if (month) params.set('month', month);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
  }
  return adminFetch(`admin-marketing?${params}`);
}

function downloadCsvFile(filename, csvText) {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'export.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function exportMarketingCsv({ type, month, from, to } = {}) {
  const data = await fetchMarketing({
    section: 'export',
    type,
    month,
    from,
    to,
  });
  if (!data?.csv) {
    throw new Error('CSV não retornado');
  }
  downloadCsvFile(data.filename, data.csv);
  return data;
}

async function updateMarketing(patch) {
  return adminFetch('admin-marketing', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

async function sendMessage(payload) {
  return adminFetch('admin-send-message', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

const adminService = {
  getAccessToken,
  getAdminUser,
  setAdminAuth,
  clearAdminAuth,
  fetchStats,
  fetchTickets,
  fetchTicket,
  ticketAction,
  deleteTickets,
  deleteRatings,
  fetchSettings,
  updateSettings,
  fetchRatings,
  fetchSiteConfigAdmin,
  updateSiteConfig,
  fetchAuditSettings,
  updateAuditSettings,
  fetchGuildResources,
  fetchGuildMembers,
  fetchWelcomeSettings,
  updateWelcomeSettings,
  fetchSession,
  fetchPermissions,
  updatePermissions,
  fetchVip,
  updateVip,
  fetchProfiles,
  updateProfiles,
  fetchTeam,
  updateTeam,
  fetchPerformance,
  fetchChangelog,
  fetchMarketing,
  updateMarketing,
  exportMarketingCsv,
  sendMessage,
};

export default adminService;
